import React, { useMemo } from 'react';
import type { ChipProps } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Chip } from 'c-components';
import type { PopDisplays_JobStatus } from 'c-sdk';
import type { TranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';
import type { JobState } from './lib';
import { deriveJobState } from './lib';

const stateColours: Record<JobState, ChipProps['color']> = {
    queued: 'default',
    running: 'primary',
    succeeded: 'success',
    failed: 'error',
    // Warning, not error: nothing has reported a failure, the job has simply been open far longer
    // than its kind ever takes.
    stuck: 'warning',
};

const stateLabels: Record<JobState, TranslationPath> = {
    queued: 'Modules.PopDisplays.JobStatus.state.queued',
    running: 'Modules.PopDisplays.JobStatus.state.running',
    succeeded: 'Modules.PopDisplays.JobStatus.state.succeeded',
    failed: 'Modules.PopDisplays.JobStatus.state.failed',
    stuck: 'Modules.PopDisplays.JobStatus.state.stuck',
};

/** Keyed on the vocabulary the API stores (CheckFargateTasks maps ECS's own states onto these). */
const ecsLabels: Record<string, TranslationPath> = {
    starting: 'Modules.PopDisplays.JobStatus.ecsState.starting',
    online: 'Modules.PopDisplays.JobStatus.ecsState.online',
    stopping: 'Modules.PopDisplays.JobStatus.ecsState.stopping',
    offline: 'Modules.PopDisplays.JobStatus.ecsState.offline',
};

type Props = {
    entity: PopDisplays_JobStatus;
};

/**
 * One chip, never two labels. The derived state and the ECS task state describe the same thing at
 * different resolutions, so stacking them reads as two competing statuses ("Queued" over
 * "Starting"). The ECS state is the more specific of the two, so it becomes the label wherever
 * there is one, and the derived state supplies the colour. A finished job is the exception: its
 * container is always gone, so "Offline" there would say nothing and contradict the green chip.
 */
const JobStateChip: React.FC<Props> = ({ entity }) => {
    const t = useCommonTranslation();
    const state = useMemo(() => deriveJobState(entity), [entity]);

    const { ecs_task_arn, ecs_task_status, memory_usage, memory_available } = entity;

    const ecsStatus = ecs_task_arn ? ecs_task_status : null;

    const label = useMemo(() => {
        if (state !== 'succeeded' && ecsStatus) {
            const known = ecsLabels[ecsStatus.toLowerCase()];
            // Unmapped values are passed through — the API stores ECS's raw state when it doesn't
            // recognise it, and a raw name beats showing nothing.
            return known ? t(known) : ecsStatus;
        }

        return t(stateLabels[state]);
    }, [t, state, ecsStatus]);

    /**
     * Memory only. The derived state used to be added here whenever the chip was showing the ECS
     * state, which put two different words for the same job in front of the user at once — a chip
     * reading "Starting" over a tooltip reading "Running". The colour already carries the derived
     * state, so naming it again has nothing to add and everything to contradict.
     *
     * memory_allowed is absent because nothing in the API writes that column; it is always 0.
     */
    const tooltip = useMemo(() => {
        if (!ecs_task_arn || memory_usage == null || memory_available == null) return null;

        return [
            t('Modules.PopDisplays.JobStatus.memory.used', { value: memory_usage }),
            t('Modules.PopDisplays.JobStatus.memory.available', { value: memory_available }),
        ].join(' / ');
    }, [t, ecs_task_arn, memory_usage, memory_available]);

    const chip = <Chip size="small" variant="outlined" color={stateColours[state]} label={label} />;

    if (tooltip == null) return chip;

    return (
        <Tooltip title={tooltip}>
            <span>{chip}</span>
        </Tooltip>
    );
};

export default JobStateChip;
